import Dexie, { type Table } from "dexie";
import type {
  AgeGroupId,
  CefrLevelId,
  EntityTypeId,
  LanguageId,
  TopicId,
} from "./references";

/**
 * Direction of study, e.g. "es>en" = front Spanish → back English.
 * Stored as a single string so it can be used directly as part of a Dexie
 * compound key without serialization.
 */
export type Direction = `${LanguageId}>${LanguageId}`;

export function makeDirection(from: LanguageId, to: LanguageId): Direction {
  return `${from}>${to}` as Direction;
}

export function parseDirection(d: Direction): [LanguageId, LanguageId] {
  const [from, to] = d.split(">") as [LanguageId, LanguageId];
  return [from, to];
}

/**
 * A language-agnostic unit of meaning ("dog", "to run", "good morning").
 * Forms in concrete languages live in {@link ConceptForm} rows so that
 * adding a new language to a pack does not rewrite existing concepts.
 */
export type Concept = {
  /** Server-assigned monotonic revision; never based on the device clock. */
  syncVersion?: number;
  id: string;
  entityType: EntityTypeId;
  /** 0..1 corpus frequency for drill ranking. */
  frequency?: number;
  topic?: TopicId;
  ageGroup?: AgeGroupId;
  level?: CefrLevelId;
  /** Pack this concept came from, or "user" for hand-created ones. */
  packId: string;
};

/**
 * Surface form of a concept in one language. `texts` holds synonyms /
 * accepted variants (the first entry is the canonical display string).
 */
export type ConceptForm = {
  /** Server-assigned monotonic revision; never based on the device clock. */
  syncVersion?: number;
  /** Composite key `${conceptId}|${lang}` — Dexie needs a single primary
      key, and an explicit string is friendlier than [conceptId, lang]
      tuples in queries. */
  key: string;
  conceptId: string;
  lang: LanguageId;
  texts: string[];
};

export function formKey(conceptId: string, lang: LanguageId): string {
  return `${conceptId}|${lang}`;
}

/**
 * Per-user SRS state for a single (concept, direction) pair. Created
 * lazily the first time the user sees the card in that direction.
 */
export type CardState = {
  /** Server-assigned monotonic revision; never based on the device clock. */
  syncVersion?: number;
  key: string; // `${conceptId}|${direction}`
  conceptId: string;
  direction: Direction;
  /** Optional deck the user filed this card under. Null for cards that
      only exist because of an installed pack. */
  deckId?: string;
  nextReview: number;
  interval: number;
  repetition: number;
  efactor: number;
};

export function cardStateKey(conceptId: string, direction: Direction): string {
  return `${conceptId}|${direction}`;
}

export type DeckRow = {
  /** Server-assigned monotonic revision; never based on the device clock. */
  syncVersion?: number;
  id: string;
  name: string;
  createdAt: number;
  sourceLang?: LanguageId;
  targetLang?: LanguageId;
  topic?: TopicId;
  ageGroup?: AgeGroupId;
  level?: CefrLevelId;
};

export type ReviewEvent = {
  id: string;
  cardKey: string;
  deckId?: string;
  reviewedAt: number;
  reviewDate: string;
  quality: number;
  isNew: boolean;
};

export type ActivityRow = {
  date: string; // YYYY-MM-DD local
  reviewed: number;
  correct: number;
};

/** Single-row tables use this sentinel id. */
export const SINGLETON_ID = "singleton" as const;

export type KvRow = { id: string; value: unknown };

export type OutboxTable = "decks" | "concepts" | "conceptForms" | "cardStates" | "reviewEvents";
export type OutboxOp = "upsert" | "delete";

/**
 * Outbox entry for cloud sync. Payload is NOT stored — the engine reads the
 * current Dexie state at flush time. Deduplication: a new enqueue for the
 * same (table, key) replaces any existing pending entry.
 */
export type OutboxRow = {
  /** Auto-increment primary key; defines FIFO flush order. */
  seq?: number;
  table: OutboxTable;
  op: OutboxOp;
  key: string;
  ts: number;
};

export class LexibitDB extends Dexie {
  concepts!: Table<Concept, string>;
  conceptForms!: Table<ConceptForm, string>;
  cardStates!: Table<CardState, string>;
  decks!: Table<DeckRow, string>;
  activity!: Table<ActivityRow, string>;
  reviewEvents!: Table<ReviewEvent, string>;
  /** Catch-all for settings, profile, installed pack manifests, migration
      flags — anything we don't want a dedicated table for. */
  kv!: Table<KvRow, string>;
  outbox!: Table<OutboxRow, number>;

  constructor() {
    super("lexibit");
    this.version(1).stores({
      // Indexes chosen for: pack browsing (packId, level), drill ranking
      // (frequency), and entity-type filters.
      concepts: "id, packId, level, entityType, frequency",
      // [conceptId+lang] lets us fetch "all forms for this concept" or
      // "this concept in language X" without a scan.
      conceptForms: "key, conceptId, lang, [conceptId+lang]",
      // nextReview is the hot path for the due queue; [direction+nextReview]
      // narrows it to the active directions in one range query.
      cardStates:
        "key, conceptId, direction, deckId, nextReview, [direction+nextReview]",
      decks: "id, createdAt",
      activity: "date",
      kv: "id",
    });
    // Version 2: adds outbox table for cloud sync.
    // [table+key] index enables deduplication on enqueue.
    this.version(2).stores({
      outbox: "++seq, [table+key], ts",
    });
    this.version(3).stores({ reviewEvents: "id, reviewedAt, reviewDate" });
  }
}

export function createDb(): LexibitDB {
  return new LexibitDB();
}

export const db = new LexibitDB();
